import { Component, OnInit } from '@angular/core';
import { Clue, HuntApiService } from '../hunt-api.service';
import { accessMessage } from '../hunt-errors';

/** The numeric settings shown on this page, in display order. */
const LIMITS: { key: string; label: string; hint: string }[] = [
  { key: 'goal', label: 'Total bones (goal)', hint: 'Shown to players as "N of 100 found"' },
  { key: 'checksPerPlayerPerMinute', label: 'Rooms a player can check per minute', hint: 'While a bone is live. Slows down scripts.' },
  { key: 'checksPerPlayerPerBone', label: 'Rooms a player can check per bone', hint: '' },
  { key: 'checksPerIpPerDay', label: 'Room checks per network per day', hint: 'Offices and colleges share one network' },
  { key: 'tokensPerIpPerDay', label: 'New players per network per day', hint: '' },
  { key: 'maxWinsPerPlayerPerDay', label: 'Wins per player per day', hint: '0 = no limit' },
  { key: 'liveMinToShow', label: 'Show "explorers online" from', hint: 'Hidden below this, so a quiet start doesn\'t look empty' },
  { key: 'explorersMinToShow', label: 'Show "explorers today" from', hint: '' },
  { key: 'liveWindowSeconds', label: 'Counts as online if seen in the last (seconds)', hint: '' },
];

@Component({
  selector: 'app-hunt-settings',
  templateUrl: './hunt-settings.component.html',
  styleUrls: ['./hunt-settings.component.scss'],
})
export class HuntSettingsComponent implements OnInit {
  readonly limits = LIMITS;
  settings: any = null;
  defaults: any = null;
  values: { [k: string]: number } = {};
  templates: Clue[] = [];
  admins: string[] = [];
  newAdmin = '';
  me = '';
  resetScope: 'found' | 'everything' = 'found';
  resetUnschedule = false;
  resetConfirm = '';
  busy = false;
  error = '';
  notice = '';

  constructor(private api: HuntApiService) {}

  ngOnInit() {
    this.api.settings().subscribe(
      (r) => {
        this.settings = r.settings;
        this.defaults = r.defaults;
        LIMITS.forEach((l) => (this.values[l.key] = r.settings[l.key]));
        this.templates = (r.settings.clueTemplates || []).map((c: Clue) => ({ ...c }));
      },
      (e) => (this.error = accessMessage(e)),
    );
    this.api.admins().subscribe((r) => (this.admins = r.admins), () => {});
    this.api.me().subscribe((r) => (this.me = r.email), () => {});
  }

  private done(msg: string) { this.busy = false; this.notice = msg; this.error = ''; window.scrollTo(0, 0); }
  private failed(e: any) { this.busy = false; this.error = accessMessage(e); window.scrollTo(0, 0); }

  saveLimits() {
    const changes: any = {};
    for (const l of LIMITS) {
      const v = Number(this.values[l.key]);
      if (!Number.isInteger(v) || v < 0) { this.error = `"${l.label}" must be a whole number.`; return; }
      if (v !== this.settings[l.key]) changes[l.key] = v;
    }
    if (!Object.keys(changes).length) { this.notice = 'Nothing changed.'; return; }
    this.busy = true;
    this.api.updateSettings(changes).subscribe((s) => { this.settings = s; this.done('Limits saved.'); }, (e) => this.failed(e));
  }

  resetLimit(key: string) { this.values[key] = this.defaults[key]; }

  addTemplate() { this.templates.push({ after: (this.templates[this.templates.length - 1] || { after: 0 }).after + 30, text: '' }); }
  removeTemplate(i: number) { this.templates.splice(i, 1); }
  saveTemplates() {
    const clean = this.templates.filter((t) => t.text.trim()).map((t) => ({ after: Number(t.after) || 0, text: t.text.trim() }));
    if (!clean.length) { this.error = 'Keep at least one clue template.'; return; }
    this.busy = true;
    this.api.updateSettings({ clueTemplates: clean }).subscribe(
      (s) => { this.settings = s; this.templates = s.clueTemplates.map((c: Clue) => ({ ...c })); this.done('Clue templates saved. They apply to the next import.'); },
      (e) => this.failed(e),
    );
  }
  restoreTemplates() { this.templates = this.defaults.clueTemplates.map((c: Clue) => ({ ...c })); }

  addAdmin() {
    const email = this.newAdmin.trim().toLowerCase();
    if (!email) return;
    this.busy = true;
    this.api.addAdmin(email).subscribe((r) => { this.admins = r.admins; this.newAdmin = ''; this.done(`${email} is now a hunt admin.`); }, (e) => this.failed(e));
  }
  removeAdmin(email: string) {
    if (!confirm(`Remove ${email} from hunt admins?`)) return;
    this.busy = true;
    this.api.removeAdmin(email).subscribe((r) => { this.admins = r.admins; this.done(`${email} removed.`); }, (e) => this.failed(e));
  }

  reset() {
    if (this.resetConfirm !== 'RESET') return;
    this.busy = true;
    this.api.reset(this.resetScope, this.resetUnschedule).subscribe(
      (r) => {
        this.resetConfirm = '';
        const parts = [`${r.claims} finds cleared`];
        if (r.bones != null) parts.push(`${r.bones} bones deleted`);
        if (r.unscheduled) parts.push('all slots cleared');
        this.done(`Hunt reset and stopped: ${parts.join(', ')}.`);
      },
      (e) => this.failed(e),
    );
  }
}
